import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId, useCurrentUser } from '../auth';
import { queryConfigs } from "@/lib/queryClient";

export interface Notification {
  id: string;
  org_id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  is_read: boolean;
  action_url?: string | null;
  metadata: any;
  created_at: string;
  read_at?: string | null;
  // Taxonomia v2
  category?: string | null;
  event_key?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  group_key?: string | null;
}

const PAGE_SIZE = 20;

export interface UseNotificationsOptions {
  unreadOnly?: boolean;
  /** Filtra por uma ou mais categorias v2 (ex.: ["deal","lead"] para a aba Vendas). */
  categories?: string[];
}

/** Prefixo comum a todas as variantes de lista (abas) — usar para invalidar/atualizar em massa. */
export const notificationsListPrefix = (orgId?: string | null, userId?: string | null) =>
  ["notifications", orgId, userId] as const;

export const notificationsCountKey = (orgId?: string | null, userId?: string | null) =>
  ["notifications-unread-count", orgId, userId] as const;

/** Opções serializadas dentro da queryKey de cada variante de lista. */
export interface NotificationsListKeyOptions {
  unreadOnly: boolean;
  categories: string | null;
}

/**
 * Lista paginada + contagem de não-lidas. Somente leitura — as mutations moram
 * em useNotificationActions (evita que cada item da lista monte uma infinite
 * query inteira só para acessar markAsRead/delete).
 */
export function useNotificationsList(options: UseNotificationsOptions = {}) {
  const { unreadOnly = false, categories } = options;
  const { orgId } = useCurrentOrgId();
  const { user } = useCurrentUser();
  const userId = user?.user_id;

  const keyOptions: NotificationsListKeyOptions = {
    unreadOnly,
    categories: categories?.length ? [...categories].sort().join(",") : null,
  };
  const listKey = [...notificationsListPrefix(orgId, userId), keyOptions];

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: listKey,
    enabled: !!orgId && !!userId,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const from = (pageParam as number) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let q = supabase
        .from("notifications")
        .select("*")
        .eq("org_id", orgId)
        .eq("user_id", userId);
      if (unreadOnly) q = q.eq("is_read", false);
      if (categories?.length) q = q.in("category", categories);
      const { data, error } = await q
        .order("created_at", { ascending: false })
        .range(from, to);
      if (error) throw error;
      return (data ?? []) as Notification[];
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === PAGE_SIZE ? allPages.length : undefined,
  });

  const notifications = data?.pages.flat() ?? [];

  // Contagem de não-lidas no servidor (não limitada à página carregada)
  const { data: unreadCount = 0 } = useQuery({
    ...queryConfigs.realtime,
    queryKey: notificationsCountKey(orgId, userId),
    enabled: !!orgId && !!userId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("org_id", orgId)
        .eq("user_id", userId)
        .eq("is_read", false);
      if (error) throw error;
      return count ?? 0;
    },
    // O caminho primário de atualização da contagem é o realtime
    // (useRealtimeNotifications invalida esta key em INSERT/UPDATE). Este
    // refetchInterval é apenas rede de segurança caso o canal realtime caia,
    // então pode ser longo — evita milhares de polls/hora por usuário.
    refetchInterval: 120000,
    refetchIntervalInBackground: false,
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
  };
}
